import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router'
import { Toaster } from 'sonner'
import React from 'react'

import { useSessionStore } from './store/auth/auth.store'
import { Login } from './pages/Login'

import { getFirstAccessibleRoute, Layout } from './components/Layout'
import { GlobalTooltip } from './components/ui/GlobalTooltip'
import { Clients } from './pages/Clients'
import { Dashboard } from './pages/Dashboard'
import { Inventory } from './pages/Inventory'
import { InventoryMovements } from './pages/InventoryMovements'
import { Loyalty } from './pages/Loyalty'
import { MultiBranch } from './pages/MultiBranch'
import { PointOfSale } from './pages/PointOfSale'
import { Purchases } from './pages/Purchases'
import { Regulatory } from './pages/Regulatory'
import { Reports } from './pages/Reports'
import { Sales } from './pages/Sales'
import { Users } from './pages/Users'
import { Sucursals } from './pages/Sucursals'
import { Categorias } from './pages/Categorias'
import { LogsAccesos } from './pages/LogsAccesos'
import { LogsAcciones } from './pages/LogsAcciones'
import { Departments } from './pages/Departments'
import { ProductTypes } from './pages/ProductTypes'
import { Transfers } from './pages/Transfers'
import { Taxes } from './pages/Taxes'
import { Units } from './pages/Unit'
import { Banners } from './pages/Banners'
import { PromotionSections } from './pages/PromotionSections'
import { Carousels } from './pages/Carousels'
import { NavigationMenus } from './pages/NavigationMenus'
// import { Notifications } from './pages/Notifications'
import { PlatformSettings } from './pages/PlatformSettings'
import { Themes } from './pages/Themes'
import { PaymentMethods } from './pages/PaymentMethods'
import { CashRegisters } from './pages/CashRegisters'
import { UseCases } from './pages/UseCases'
import { CashHistory } from './pages/CashHistory'
import { Doctors } from './pages/Doctors'
import { Banks } from './pages/Banks'
import { Promotions } from './pages/Promotions'
import { Popups } from './pages/Popups'
import { PublishedProducts } from './pages/PublishedProducts'
import { POPUPS_FRONTEND_PREVIEW_ENABLED } from './components/popups/popup-access'
import { StoreSettings } from './pages/StoreSettings'
import { StoreDesign } from './pages/StoreDesign'
import { StoreOrders } from './pages/StoreOrders'
import { StorePaymentMethods } from './pages/StorePaymentMethods'

// Wrapper para proteger rutas
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useSessionStore((s) => s.token)
  const location = useLocation()

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
function PermissionRoute({
  permission,
  children,
}: {
  permission: string | string[]
  children: React.ReactNode
}) {
  const canAction = useSessionStore((s) => s.canAction)
  const permissions = useSessionStore((s) => s.permissions)
  const permissionsLoaded = useSessionStore((s) => s.user?.permissions !== undefined)

  if (!permissionsLoaded) {
    return null
  }

  const canAccess = Array.isArray(permission)
    ? permission.some((action) => canAction(action))
    : canAction(permission)

  if (!canAccess) {
    const destination = getFirstAccessibleRoute(permissions)

    return destination ? <Navigate to={destination} replace /> : null
  }

  return <>{children}</>
}
export default function App() {
  const token = useSessionStore((s) => s.token)

  return (
    <BrowserRouter>
      <Routes>
        {/* Ruta pública / Login */}
        <Route
          path="/login"
          element={
            token ? (
              <Navigate to="/" replace />
            ) : (
              <Login onAuthenticated={() => {
                // Token is set inside Login via authStore, redirect is handled by react-router on re-render
              }} />
            )
          }
        />

        {/* Rutas Protegidas */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<PermissionRoute permission="dashboard.dashboard.view"><Dashboard /></PermissionRoute>} />
                  <Route path="/pos" element={<PermissionRoute permission="pos.pos.view"><PointOfSale /></PermissionRoute>} />
                  <Route path="/historial-caja" element={<PermissionRoute permission="configuration.cash.view"><CashHistory /></PermissionRoute>} />
                  <Route path="/ventas" element={<PermissionRoute permission="sales.sale.view"><Sales /></PermissionRoute>} />
                  <Route path="/promociones" element={<PermissionRoute permission="promotions.promotion.view"><Promotions /></PermissionRoute>} />
                  <Route path="/catalogo" element={<PermissionRoute permission="inventory.catalog.view"><Inventory /></PermissionRoute>} />
                  <Route path="/promociones" element={<PermissionRoute permission="promotions.promotion.view"><Promotions /></PermissionRoute>} />
                  <Route path="/inventario/movimientos" element={<PermissionRoute permission="inventory.traceability.view"><InventoryMovements /></PermissionRoute>} />
                  <Route path="/inventario/transferencias" element={<PermissionRoute permission="inventory.catalog-transfer.view"><Transfers /></PermissionRoute>} />
                  <Route path="/compras" element={<PermissionRoute permission={['purchases.purchase.view', 'purchases.supplier.view', 'purchases.account_payable.view']}><Purchases /></PermissionRoute>} />
                  <Route path="/reportes" element={<PermissionRoute permission={['reports.sales.view', 'reports.inventory.view', 'reports.cash-movements.view', 'reports.cash-registers.view', 'reports.sale-returns.view', 'reports.orders.view', 'reports.purchases.view', 'reports.cfdi.view']}><Reports /></PermissionRoute>} />
                  <Route path="/clientes" element={<PermissionRoute permission="clients.customer.view"><Clients /></PermissionRoute>} />
                  <Route path="/lealtad" element={<PermissionRoute permission="loyalty.loyalty.view"><Loyalty /></PermissionRoute>} />
                  <Route path="/usuarios" element={<PermissionRoute permission="users.user.view"><Users /></PermissionRoute>} />
                  <Route path="/regulatorio" element={<Regulatory />} />
                  <Route path="/dashboard-sucursales" element={<PermissionRoute permission="dashboard.dashboard.view"><MultiBranch /></PermissionRoute>} />
                  <Route path="/sucursales" element={<PermissionRoute permission="sucursals.sucursal.view"><Sucursals /></PermissionRoute>} />
                  <Route path="/categorias" element={<PermissionRoute permission="inventory.category.view"><Categorias /></PermissionRoute>} />
                  <Route path="/logs-accesos" element={<PermissionRoute permission="logs.access-log.view"><LogsAccesos /></PermissionRoute>} />
                  <Route path="/logs-acciones" element={<PermissionRoute permission="logs.activity-log.view"><LogsAcciones /></PermissionRoute>} />
                  <Route path="/departamentos" element={<PermissionRoute permission="inventory.department.view"><Departments /></PermissionRoute>} />
                  <Route path="/tipos-producto" element={<PermissionRoute permission="inventory.product-type.view"><ProductTypes /></PermissionRoute>} />
                  <Route path="/casos-de-uso" element={<PermissionRoute permission="inventory.use-case.view"><UseCases /></PermissionRoute>} />
                  <Route path="/impuestos" element={<PermissionRoute permission="configuration.tax.view"><Taxes /></PermissionRoute>} />
                  <Route path="/unidades-medida" element={<PermissionRoute permission="configuration.unit.view"><Units /></PermissionRoute>} />
                  <Route path="/metodos-pago" element={<PermissionRoute permission="configuration.payment-method.view"><PaymentMethods /></PermissionRoute>} />
                  <Route path="/cash-registers" element={<PermissionRoute permission="configuration.cash.view"><CashRegisters /></PermissionRoute>} />
                  <Route path="/configuracion/plataforma" element={<PermissionRoute permission="configuration.configuration.view"><PlatformSettings /></PermissionRoute>} />
                  <Route path="/configuracion/temas" element={<PermissionRoute permission="configuration.configuration.view"><Themes /></PermissionRoute>} />
                  <Route path="/doctores" element={<PermissionRoute permission="doctors.doctor.view"><Doctors /></PermissionRoute>} />
                  <Route path="/bancos" element={<PermissionRoute permission="configuration.bank.view"><Banks /></PermissionRoute>} />
                  <Route path="/tienda/configuracion" element={<PermissionRoute permission="configuration.configuration.view"><StoreSettings /></PermissionRoute>} />
                  <Route path="/tienda/diseno" element={<PermissionRoute permission="ecommerce.home_component.view"><StoreDesign /></PermissionRoute>} />
                  <Route path="/tienda/banners" element={<PermissionRoute permission="ecommerce.banner.view"><Banners /></PermissionRoute>} />
                  <Route path="/tienda/carruseles" element={<PermissionRoute permission="ecommerce.carousel.view"><Carousels /></PermissionRoute>} />
                  <Route path="/tienda/secciones-promociones" element={<PromotionSections />} />
                  <Route path="/tienda/navbar" element={<PermissionRoute permission="ecommerce.navigation-menu.view"><NavigationMenus /></PermissionRoute>} />
                  <Route path="/tienda/productos" element={<PermissionRoute permission="ecommerce.catalog.view"><PublishedProducts /></PermissionRoute>} />
                  <Route path="/tienda/metodos-pago" element={<PermissionRoute permission="configuration.payment-method.view"><StorePaymentMethods /></PermissionRoute>} />
                  <Route
                    path="/tienda/pedidos"
                    element={
                      <PermissionRoute permission="ecommerce.order.view">
                        <StoreOrders />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/tienda/popups"
                    element={
                      POPUPS_FRONTEND_PREVIEW_ENABLED
                        ? <Popups />
                        : <PermissionRoute permission="online-store.popup.view"><Popups /></PermissionRoute>
                    }
                  />
                  {/* <Route path="/notificaciones" element={<Notifications />} /> */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <GlobalTooltip />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  )
}
